import { useTranslation } from 'react-i18next'
import type { AuditEntry } from '@/services/shared/auditHistoryApi'
import { formatAbsoluteTime, formatRelativeTime } from '../utils/relativeTime'
import { auditMessageText } from '../utils/auditMessage'

export interface AuditTimelineProps {
  entries: AuditEntry[]
  /**
   * Caps how many of an entry's messages are listed, the rest summarised as a
   * count. Left out, every message is shown.
   */
  maxMessagesPerEntry?: number
}

/**
 * A change history as a timeline: one node per save, the messages of that save
 * beneath it.
 *
 * Each message carries a `code` and an English `text` template, so it is
 * translated by code where a translation exists and falls back to the text the
 * backend shipped where it does not.
 */
export function AuditTimeline({ entries, maxMessagesPerEntry }: AuditTimelineProps) {
  const { t, i18n } = useTranslation()

  return (
    <ol className="flex flex-col">
      {entries.map((entry, index) => {
        const shown = maxMessagesPerEntry ? entry.messages.slice(0, maxMessagesPerEntry) : entry.messages
        const hidden = entry.messages.length - shown.length
        return (
          <li key={`${entry.timestamp}-${index}`} className="relative flex gap-3 pb-4 last:pb-0">
            {/* Connector down to the next node — not drawn past the last one. */}
            {index < entries.length - 1 && (
              <span className="absolute top-3 bottom-0 left-[3px] w-px bg-(--ad-border)" aria-hidden />
            )}
            <span className="mt-1.5 size-[7px] shrink-0 rounded-full bg-(--ad-primary)" aria-hidden />
            <div className="min-w-0 flex-1">
              <div className="text-xs text-(--ad-text-muted)">
                <span className="font-semibold text-(--ad-text)">
                  {entry.user?.name || t('audit.unknownUser')}
                </span>
                {entry.masqueraderUser && ` ${t('audit.masqueradedBy', { name: entry.masqueraderUser.name })}`}
                {' · '}
                <time dateTime={entry.timestamp} title={formatAbsoluteTime(entry.timestamp)}>
                  {formatRelativeTime(entry.timestamp, i18n.language)}
                </time>
              </div>
              <ul className="mt-0.5 flex flex-col gap-0.5 text-sm">
                {shown.map((message, messageIndex) => (
                  <li key={`${message.code}-${messageIndex}`}>{auditMessageText(t, message)}</li>
                ))}
                {hidden > 0 && (
                  <li className="text-xs text-(--ad-text-muted)">{t('audit.moreChanges', { count: hidden })}</li>
                )}
              </ul>
            </div>
          </li>
        )
      })}
    </ol>
  )
}
